// Frontend fetchers for the STT Arena. All calls hit the Bun dashboard, which
// proxies to the FastAPI Arena service. Plain fetch — matches the dashboard convention.

import type {
  ArenaConfig,
  ArenaSample,
  BenchmarkOptions,
  BenchmarkJudgeEvaluationContext,
  BenchmarkPreview,
  BenchmarkKeytermPairsPage,
  BenchmarkRowsPage,
  BenchmarkRun,
  BenchmarkRunConfig,
  BenchmarkReview,
  BenchmarkReferenceReview,
  BenchmarkReferenceReviewsPage,
  KeytermPreparation,
  JudgeCalibration,
  JudgeCalibrationItem,
  JudgeCalibrationSummary,
  DatasetPage,
  Rankings,
  StudentLabelStatus,
  StudentLabelSummary,
  StudentAssignment,
  StudentAssignmentPage,
  StudentDirectoryUser,
  StudentSample,
  StudentSamplesPage,
} from "./types";

const BASE = "/api/internal/stt-arena";

async function getJSON<T>(path: string, timeoutMs?: number): Promise<T> {
  const signal = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
  try {
    const r = await fetch(`${BASE}${path}`, {
      cache: "no-store", signal,
    });
    if (!r.ok) throw new Error((await safeDetail(r)) || `GET ${path} failed (${r.status})`);
    return await r.json() as T;
  } catch (cause) {
    if (signal?.aborted) {
      throw new Error(`GET ${path} timed out. Retry the request.`);
    }
    throw cause;
  }
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await safeDetail(r)) || `POST ${path} failed (${r.status})`);
  return r.json() as Promise<T>;
}

async function putJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await safeDetail(r)) || `PUT ${path} failed (${r.status})`);
  return r.json() as Promise<T>;
}

async function deleteJSON<T>(path: string): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { method: "DELETE" });
  if (!r.ok) throw new Error((await safeDetail(r)) || `DELETE ${path} failed (${r.status})`);
  return r.json() as Promise<T>;
}

async function safeDetail(r: Response): Promise<string | null> {
  try {
    const j = await r.clone().json();
    return (j as { detail?: string }).detail ?? null;
  } catch {
    return null;
  }
}

export const arenaApi = {
  config: () => getJSON<ArenaConfig>("/accents"),
  rankings: () => getJSON<Rankings>("/rankings", 10_000),
  dataset: (opts: { limit?: number; offset?: number; accent?: string; condition?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit != null) p.set("limit", String(opts.limit));
    if (opts.offset != null) p.set("offset", String(opts.offset));
    if (opts.accent) p.set("accent", opts.accent);
    if (opts.condition) p.set("condition", opts.condition);
    return getJSON<DatasetPage>(`/dataset?${p.toString()}`);
  },
  samples: () => getJSON<{ items: ArenaSample[] }>("/samples"),
  generateSentence: (subject?: string) =>
    postJSON<{ text: string; subject: string }>("/generate-sentence", { subject }),
  uploadRecording: async (form: FormData): Promise<ArenaSample> => {
    const r = await fetch(`${BASE}/recordings`, { method: "POST", body: form });
    if (!r.ok) throw new Error((await safeDetail(r)) || `Upload failed (${r.status})`);
    return r.json() as Promise<ArenaSample>;
  },
  addNoise: (sampleId: string) => postJSON<ArenaSample>("/noise", { sample_id: sampleId }),
  run: (sampleId: string, force = false) =>
    postJSON<{ sample: ArenaSample; results: ArenaSample["results"]; live: Rankings["live"] }>(
      "/run",
      { sample_id: sampleId, force },
    ),
  studentSamples: (opts: {
    datasetVersion?: string;
    cohort?: string;
    split?: string;
    durationBand?: string;
    labelStatus?: string;
    assignedTo?: string;
    assignmentId?: string;
    assignmentStatus?: string;
    unassigned?: boolean;
    limit?: number;
    offset?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (opts.datasetVersion) p.set("dataset_version", opts.datasetVersion);
    if (opts.cohort) p.set("cohort", opts.cohort);
    if (opts.split) p.set("split", opts.split);
    if (opts.durationBand) p.set("duration_band", opts.durationBand);
    if (opts.labelStatus) p.set("label_status", opts.labelStatus);
    if (opts.assignedTo) p.set("assigned_to", opts.assignedTo);
    if (opts.assignmentId) p.set("assignment_id", opts.assignmentId);
    if (opts.assignmentStatus) p.set("assignment_status", opts.assignmentStatus);
    if (opts.unassigned) p.set("unassigned", "true");
    if (opts.limit != null) p.set("limit", String(opts.limit));
    if (opts.offset != null) p.set("offset", String(opts.offset));
    return getJSON<StudentSamplesPage>(`/student-samples?${p.toString()}`);
  },
  studentSummary: (datasetVersion?: string) => {
    const p = new URLSearchParams();
    if (datasetVersion) p.set("dataset_version", datasetVersion);
    return getJSON<StudentLabelSummary>(`/student-samples/summary?${p.toString()}`);
  },
  labelStudentSample: (
    sampleId: string,
    body: {
      label_status: StudentLabelStatus;
      evaluation_type: StudentSample["evaluation_type"];
      transcript: string | null;
      reference_text: string | null;
      label_reason: string | null;
      transcribability: StudentSample["transcribability"];
      reference_scope: StudentSample["reference_scope"];
      review_notes: string | null;
      label_source: "human";
      labeled_by: string;
      reviewer_id: string;
      adjudication_status: "pending" | "approved";
    },
  ) => putJSON<StudentSample>(`/student-samples/${encodeURIComponent(sampleId)}/label`, body),
  suggestStudentLabel: (sampleId: string, force = false) =>
    postJSON<StudentSample>(`/student-samples/${encodeURIComponent(sampleId)}/suggest-label`, { force }),
  assignmentPreview: (body: {
    requested_count: number;
    assignee_ids: string[];
    filters: { dataset_version?: string; cohort?: string; split?: string; duration_band?: string; label_status?: string };
    seed?: number;
  }) => postJSON<StudentAssignment>("/student-assignments/preview", body),
  commitAssignment: (assignmentId: string, idempotencyKey?: string) =>
    postJSON<StudentAssignment>("/student-assignments", {
      assignment_id: assignmentId,
      idempotency_key: idempotencyKey,
    }),
  assignments: (opts: { datasetVersion?: string; assigneeId?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.datasetVersion) p.set("dataset_version", opts.datasetVersion);
    if (opts.assigneeId) p.set("assignee_id", opts.assigneeId);
    if (opts.status) p.set("status", opts.status);
    if (opts.limit != null) p.set("limit", String(opts.limit));
    if (opts.offset != null) p.set("offset", String(opts.offset));
    return getJSON<StudentAssignmentPage>(`/student-assignments?${p.toString()}`);
  },
  releaseAssignment: (assignmentId: string) =>
    postJSON<StudentAssignment>(`/student-assignments/${encodeURIComponent(assignmentId)}/release`, {}),
  assignableUsers: () => getJSON<{ users: Array<{ id: string }> }>("/student-assignments/assignable-users"),
  directoryUsers: async (userIds: string[]) => {
    // Keep each request within the backend's validation limit as the roster grows.
    const chunks: string[][] = [];
    for (let index = 0; index < userIds.length; index += 100) {
      chunks.push(userIds.slice(index, index + 100));
    }
    const responses = await Promise.all(chunks.map(async (chunk) => {
      const response = await fetch("/api/internal/admin/users/by-ids", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userIds: chunk }),
      });
      if (!response.ok) {
        throw new Error((await safeDetail(response)) || `User details lookup failed (${response.status})`);
      }
      return response.json() as Promise<{ users: StudentDirectoryUser[] }>;
    }));
    return { users: responses.flatMap((response) => response.users) };
  },
  benchmarkOptions: () => getJSON<BenchmarkOptions>("/benchmark-runs/options"),
  benchmarkPreview: (config: BenchmarkRunConfig) =>
    postJSON<BenchmarkPreview>("/benchmark-runs/preview", { config }),
  createKeytermPreparation: (
    config: BenchmarkRunConfig,
    idempotencyKey: string,
    options: { forceRegenerate?: boolean } = {},
  ) =>
    postJSON<KeytermPreparation>("/benchmark-runs/keyterm-preparations", {
      config,
      idempotency_key: idempotencyKey,
      force_regenerate: options.forceRegenerate ?? false,
    }),
  keytermPreparation: (preparationId: string) =>
    getJSON<KeytermPreparation>(
      `/benchmark-runs/keyterm-preparations/${encodeURIComponent(preparationId)}`,
    ),
  regenerateKeytermPreparation: (
    preparationId: string,
    idempotencyKey: string,
  ) =>
    postJSON<KeytermPreparation>(
      `/benchmark-runs/keyterm-preparations/${encodeURIComponent(preparationId)}/regenerate`,
      { idempotency_key: idempotencyKey },
    ),
  createBenchmarkRun: (
    config: BenchmarkRunConfig,
    previewHash: string,
    idempotencyKey: string,
    keytermPreparationId?: string,
  ) =>
    postJSON<BenchmarkRun>("/benchmark-runs", {
      config,
      preview_hash: previewHash,
      idempotency_key: idempotencyKey,
      keyterm_preparation_id: keytermPreparationId,
    }),
  benchmarkRuns: () =>
    getJSON<{ total: number; items: BenchmarkRun[] }>("/benchmark-runs", 10_000),
  benchmarkRun: (runId: string) =>
    getJSON<BenchmarkRun>(`/benchmark-runs/${encodeURIComponent(runId)}`),
  rebuildBenchmarkSummary: (runId: string) =>
    postJSON<BenchmarkRun>(
      `/benchmark-runs/${encodeURIComponent(runId)}/rebuild-summary`,
      {},
    ),
  benchmarkJudgeEvaluations: (runId: string) =>
    getJSON<{ total: number; items: BenchmarkJudgeEvaluationContext[] }>(
      `/benchmark-runs/${encodeURIComponent(runId)}/judge-evaluations`,
    ),
  benchmarkJudgeEvaluation: (runId: string, evaluationId: string) =>
    getJSON<BenchmarkJudgeEvaluationContext>(
      `/benchmark-runs/${encodeURIComponent(runId)}/judge-evaluations/${encodeURIComponent(evaluationId)}`,
    ),
  createBenchmarkJudgeEvaluation: (
    runId: string,
    options: { policyVersion?: string } = {},
  ) =>
    postJSON<BenchmarkJudgeEvaluationContext>(
      `/benchmark-runs/${encodeURIComponent(runId)}/judge-evaluations`,
      { policy_version: options.policyVersion },
    ),
  activateBenchmarkJudgeEvaluation: (runId: string, evaluationId: string) =>
    postJSON<BenchmarkRun>(
      `/benchmark-runs/${encodeURIComponent(runId)}/judge-evaluations/${encodeURIComponent(evaluationId)}/activate`,
      {},
    ),
  benchmarkRows: (
    runId: string,
    opts: {
      providerKey?: string;
      decision?: string;
      reviewStatus?: string;
      errorType?: string;
      keytermMode?: string;
      keytermSource?: string;
      unclearReason?: string;
      referenceQuality?: string;
      judgeEvaluationId?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.providerKey) params.set("provider_key", opts.providerKey);
    if (opts.decision) params.set("decision", opts.decision);
    if (opts.reviewStatus) params.set("review_status", opts.reviewStatus);
    if (opts.errorType) params.set("error_type", opts.errorType);
    if (opts.keytermMode) params.set("keyterm_mode", opts.keytermMode);
    if (opts.keytermSource) params.set("keyterm_source", opts.keytermSource);
    if (opts.unclearReason) params.set("unclear_reason", opts.unclearReason);
    if (opts.referenceQuality) params.set("reference_quality", opts.referenceQuality);
    if (opts.judgeEvaluationId) params.set("judge_evaluation_id", opts.judgeEvaluationId);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.offset != null) params.set("offset", String(opts.offset));
    return getJSON<BenchmarkRowsPage>(
      `/benchmark-runs/${encodeURIComponent(runId)}/rows?${params.toString()}`,
    );
  },
  benchmarkReferenceReviews: (
    runId: string,
    referenceQuality: "valid" | "suspect" | "unknown" = "unknown",
  ) =>
    getJSON<BenchmarkReferenceReviewsPage>(
      `/benchmark-runs/${encodeURIComponent(runId)}/reference-reviews?reference_quality=${referenceQuality}`,
    ),
  reviewBenchmarkReference: (
    runId: string,
    sampleId: string,
    referenceQuality: "valid" | "suspect" | "unknown",
    notes: string | null,
  ) =>
    putJSON<BenchmarkReferenceReview>(
      `/benchmark-runs/${encodeURIComponent(runId)}/reference-reviews/${encodeURIComponent(sampleId)}`,
      { reference_quality: referenceQuality, notes },
    ),
  benchmarkKeytermPairs: (
    runId: string,
    opts: {
      providerKey?: string;
      category?: string;
      outcome?: string;
      judgeEvaluationId?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.providerKey) params.set("provider_key", opts.providerKey);
    if (opts.category) params.set("category", opts.category);
    if (opts.outcome) params.set("outcome", opts.outcome);
    if (opts.judgeEvaluationId) params.set("judge_evaluation_id", opts.judgeEvaluationId);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.offset != null) params.set("offset", String(opts.offset));
    return getJSON<BenchmarkKeytermPairsPage>(
      `/benchmark-runs/${encodeURIComponent(runId)}/keyterm-pairs?${params.toString()}`,
    );
  },
  cancelBenchmarkRun: (runId: string) =>
    postJSON<BenchmarkRun>(
      `/benchmark-runs/${encodeURIComponent(runId)}/cancel`,
      {},
    ),
  resumeBenchmarkRun: (runId: string) =>
    postJSON<BenchmarkRun>(
      `/benchmark-runs/${encodeURIComponent(runId)}/resume`,
      {},
    ),
  reviewBenchmarkRow: (
    runId: string,
    cellId: string,
    humanVerdict: "equivalent" | "material_error" | "unclear",
    notes: string | null,
    options: {
      severity?: "none" | "non_critical" | "critical" | "unknown";
      errorTypes?: string[];
      referenceIssueSuspected?: boolean;
      referenceQuality?: "valid" | "suspect" | "unknown";
    } = {},
  ) =>
    putJSON<BenchmarkReview>(
      `/benchmark-runs/${encodeURIComponent(runId)}/rows/${encodeURIComponent(cellId)}/review`,
      {
        human_verdict: humanVerdict,
        notes,
        severity: options.severity,
        error_types: options.errorTypes,
        reference_issue_suspected: options.referenceIssueSuspected,
        reference_quality: options.referenceQuality,
      },
    ),
  adjudicateBenchmarkRow: (
    runId: string,
    cellId: string,
    humanVerdict: "equivalent" | "material_error" | "unclear",
    notes: string | null,
    options: { severity?: "none" | "non_critical" | "critical" | "unknown"; errorTypes?: string[]; referenceQuality?: "valid" | "suspect" | "unknown" } = {},
  ) => putJSON<BenchmarkReview>(
    `/benchmark-runs/${encodeURIComponent(runId)}/rows/${encodeURIComponent(cellId)}/adjudication`,
    { human_verdict: humanVerdict, notes, severity: options.severity, error_types: options.errorTypes, reference_quality: options.referenceQuality },
  ),
  judgeCalibrations: () => getJSON<{ total: number; items: JudgeCalibration[] }>("/judge-calibrations"),
  createJudgeCalibration: (sourceRunId: string, phase: "development" | "validation", seed = 42) =>
    postJSON<JudgeCalibration>("/judge-calibrations", { source_run_id: sourceRunId, phase, seed }),
  archiveJudgeCalibration: (calibrationId: string) =>
    deleteJSON<JudgeCalibration>(`/judge-calibrations/${encodeURIComponent(calibrationId)}`),
  judgeCalibration: (calibrationId: string) =>
    getJSON<JudgeCalibrationSummary>(`/judge-calibrations/${encodeURIComponent(calibrationId)}`),
  judgeCalibrationItems: (calibrationId: string, pendingOnly = false) =>
    getJSON<{ items: JudgeCalibrationItem[] }>(
      `/judge-calibrations/${encodeURIComponent(calibrationId)}/items${pendingOnly ? "?pending_only=true" : ""}`,
    ),
  reviewJudgeCalibrationItem: (
    calibrationId: string,
    itemId: string,
    humanVerdict: "equivalent" | "material_error" | "unclear",
    severity: "none" | "non_critical" | "critical" | "unknown",
    notes: string | null,
  ) => putJSON(
    `/judge-calibrations/${encodeURIComponent(calibrationId)}/items/${encodeURIComponent(itemId)}/review`,
    { human_verdict: humanVerdict, severity, notes },
  ),
  adjudicateJudgeCalibrationItem: (
    calibrationId: string,
    itemId: string,
    humanVerdict: "equivalent" | "material_error" | "unclear",
    severity: "none" | "non_critical" | "critical" | "unknown",
    notes: string | null,
  ) => putJSON(
    `/judge-calibrations/${encodeURIComponent(calibrationId)}/items/${encodeURIComponent(itemId)}/adjudication`,
    { human_verdict: humanVerdict, severity, notes },
  ),
  evaluateJudgeCalibration: (calibrationId: string, contract: Record<string, string> = {}) =>
    postJSON<{ calibration_id: string; evaluation_id: string; item_count: number }>(
      `/judge-calibrations/${encodeURIComponent(calibrationId)}/evaluations`,
      contract,
    ),
};

// Resolve an audio URI to a playable URL. s3:// objects stream through the
// Arena service (same-origin) so neither waveform decoding nor playback depends on the
// S3 bucket's CORS config. http/blob URLs (e.g. a freshly recorded clip) pass
// through unchanged.
export function resolveAudioUrl(uri: string): Promise<string> {
  if (!uri.startsWith("s3://")) return Promise.resolve(uri);
  return Promise.resolve(`${BASE}/audio?uri=${encodeURIComponent(uri)}`);
}
